import { fn } from '@ember/helper';
import { gt } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import didIntersect from 'ember-scroll-modifiers/modifiers/did-intersect';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import safeFormatTime from '../../helpers/safe-format-time.ts';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import type EmployeeModel from 'tio-data/models/employee';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type NotificationHistoryModel from 'tio-data/models/notification-history';

interface NotificationHistoriesTableSignature {
  Args: {
    employee: EmployeeModel;
    noteSubtypeFilter: string;
    limit: number;
  };
}

export default class NotificationHistoriesTableComponent extends Component<NotificationHistoriesTableSignature> {
  @service declare store: StoreService;
  @tracked page = 1;
  @tracked isInViewport = false;

  get isLoading() {
    return this.notificationHistoriesData.isLoading;
  }

  notificationHistoriesData = trackedFunction(this, async () => {
    if (!this.args.employee) {
      return [];
    }
    const limit = this.args.limit || 10;
    const page = {
      limit: limit,
      offset: (this.page - 1) * limit,
    };

    const filter = {
      employee: this.args.employee.id,
    };

    if (this.args.noteSubtypeFilter) {
      // @ts-expect-error: figure out better solution for generic filters.
      filter.noteSubtype = this.args.noteSubtypeFilter;
    }

    const sort = '-created_at';
    return (await this.store.query('notification-history', {
      filter,
      page,
      sort,
    })) as NotificationHistoryModel[];
  });

  get notificationHistories() {
    return this.notificationHistoriesData?.value || [];
  }

  previousPage = () => {
    this.page = Math.max(1, this.page - 1);
  };

  nextPage = () => {
    ++this.page;
  };

  changePaginatorPage = (page: number) => {
    this.page = page;
  };

  get totalPages() {
    // @ts-expect-error: Meta property type not properly typed on collection
    const meta = this.notificationHistories?.meta;

    if (meta?.page.total) {
      return Math.ceil(meta.page.total / this.args.limit);
    }
    return 0;
  }

  <template>
    <span {{didIntersect onEnter=(fn (mut this.isInViewport) true)}} />
    {{#if this.isInViewport}}
      <Table @isLoading={{this.isLoading}}>
        <:thead as |options|>
          <options.tr>
            <options.th class="w-48">{{t
                "common.profile.notification_histories.sent_on"
              }}</options.th>
            <options.th>{{t "common.profile.notification_histories.email_type"}}</options.th>
            <options.th>{{t "common.profile.notification_histories.sent_to"}}</options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each this.notificationHistories as |history|}}
            <options.tr>
              <options.td>
                {{safeFormatDate history.createdAt month="short" day="2-digit" year="numeric"}}
                {{safeFormatTime
                  history.createdAt
                  hour="numeric"
                  minute="numeric"
                  second="numeric"
                }}
              </options.td>
              <options.td>{{history.noteSubtype}}</options.td>
              <options.td>{{history.toEmail}}</options.td>
            </options.tr>
          {{else}}
            <options.tr>
              <options.td colspan="3">
                {{t "common.profile.notification_histories.no_communications"}}
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
        <:tfoot>
          <tr>
            <td class="text-right" colspan="9">
              {{#if (gt this.totalPages 1)}}
                <Paginator
                  @simple={{true}}
                  @currentPage={{this.page}}
                  {{! @glint-expect-error }}
                  @totalItems={{this.notificationHistories.meta.page.total}}
                  @pageSize={{@limit}}
                  @onChange={{this.changePaginatorPage}}
                />
              {{/if}}
            </td>
          </tr>
        </:tfoot>
      </Table>
    {{/if}}
  </template>
}
